import React from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";

import AppLayout from "../../components/common/layout/AppLayout";
import SuspenseFallback from "../../components/common/SuspenseFallback";
import { useAuth } from "../../lib/auth";
import NotFound from "../NotFound";
import ServerDown from "../ServerDown";
import { ProtectedRoute } from "../../helpers/ProtectedRoute";
import { actionName, actionUrl, moduleName } from "../../constants/defaultValues";

const Dashboard = React.lazy(() => import("./Dashboard/Dashboard"));
const POS = React.lazy(() => import("./POS/POS"));
const SalesHistory = React.lazy(() => import("./POS/SalesHistory"));
const Products = React.lazy(() => import("./Inventory/Products"));
const InboundParents = React.lazy(() => import("./Inventory/InboundParents"));
const Grn = React.lazy(() => import("./Grn/Grn"));
const Customers = React.lazy(() => import("./Customers/Customers"));
const Registers = React.lazy(() => import("./Registers/Registers"));
const Companies = React.lazy(() => import("./Companies/Companies"));
const ExpenseCategories = React.lazy(() => import("./ExpenseCategories/ExpenseCategories"));
const Expenses = React.lazy(() => import("./Expenses/Expenses"));
const Discounts = React.lazy(() => import("./Discounts/Discounts"));
const Memberships = React.lazy(() => import("./Memberships/Memberships"));
const Loyalty = React.lazy(() => import("./Loyalty/Loyalty"));
const CreditNotes = React.lazy(() => import("./CreditNotes/CreditNotes"));
const Returns = React.lazy(() => import("./Returns/Returns"));
const MultiChallan = React.lazy(() => import("./MultiChallan/MultiChallan"));
const MultiInvoice = React.lazy(() => import("./MultiInvoice/MultiInvoice"));
const Stores = React.lazy(() => import("./Stores"));
const Settings = React.lazy(() => import("./Settings/Settings"));
const Gst = React.lazy(() => import("./Gst"));
const Profile = React.lazy(() => import("./Profile/Profile"));
const Users = React.lazy(() => import("./Users"));
const Roles = React.lazy(() => import("./Roles"));
const StoreTypeAccess = React.lazy(() => import("./StoreTypeAccess"));
const AuditLog = React.lazy(() => import("./AuditLog"));
const SalesReport = React.lazy(() => import("./Reports/SalesReport"));
const StockAgingReport = React.lazy(() => import("./Reports/StockAgingReport"));
const ArticleMovementReport = React.lazy(() => import("./Reports/ArticleMovementReport"));
const TradingPlReport = React.lazy(() => import("./Accounting/TradingPlReport"));
const AccountingSettings = React.lazy(() => import("./Accounting/AccountingSettings"));
const PurchaseSaleReport = React.lazy(() => import("./Accounting/PurchaseSaleReport"));

function PrivateLayout() {
  const { activeStoreId } = useAuth();
  return (
    <AppLayout>
      {/* Remount route content on store switch so every list refetches against the new X-Store-Id. */}
      <React.Suspense key={activeStoreId ?? "global"} fallback={<SuspenseFallback />}>
        <Routes>
          <Route
            path="/"
            element={<ProtectedRoute element={<Dashboard />} options={{ isAlwaysAllowed: true }} />}
          />
          <Route
            path="pos/history/*"
            element={
              <ProtectedRoute
                element={<SalesHistory />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.POS,
                      method: actionName.GET,
                      actionUrl: actionUrl.POS_VIEW,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="pos/*"
            element={
              <ProtectedRoute
                element={<POS />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.POS,
                      method: actionName.GET,
                      actionUrl: actionUrl.POS_VIEW,
                    },
                  ],
                }}
              />
            }
          />
          <Route path="inventory" element={<Navigate to="/inventory/products" replace />} />
          <Route
            path="inventory/products/*"
            element={
              <ProtectedRoute
                element={<Products />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.PRODUCTS,
                      method: actionName.GET,
                      actionUrl: actionUrl.PRODUCT_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="inventory/inbound-parents/*"
            element={
              <ProtectedRoute
                element={<InboundParents />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.PRODUCTS,
                      method: actionName.GET,
                      actionUrl: actionUrl.INBOUND_PARENT_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="grn/*"
            element={
              <ProtectedRoute
                element={<Grn />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.GRN,
                      method: actionName.GET,
                      actionUrl: actionUrl.GRN_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="customers/*"
            element={
              <ProtectedRoute
                element={<Customers />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.CUSTOMER,
                      method: actionName.GET,
                      actionUrl: actionUrl.CUSTOMER_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="registers/*"
            element={
              <ProtectedRoute
                element={<Registers />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.REGISTER,
                      method: actionName.GET,
                      actionUrl: actionUrl.REGISTER_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="companies/*"
            element={
              <ProtectedRoute
                element={<Companies />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.COMPANY,
                      method: actionName.GET,
                      actionUrl: actionUrl.COMPANY_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="expense-categories/*"
            element={
              <ProtectedRoute
                element={<ExpenseCategories />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.EXPENSE,
                      method: actionName.GET,
                      actionUrl: actionUrl.EXPENSE_CATEGORY_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="expenses/*"
            element={
              <ProtectedRoute
                element={<Expenses />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.EXPENSE,
                      method: actionName.GET,
                      actionUrl: actionUrl.EXPENSE_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="discounts/*"
            element={
              <ProtectedRoute
                element={<Discounts />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.DISCOUNT,
                      method: actionName.GET,
                      actionUrl: actionUrl.DISCOUNT_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="memberships/*"
            element={
              <ProtectedRoute
                element={<Memberships />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.MEMBERSHIP,
                      method: actionName.GET,
                      actionUrl: actionUrl.MEMBERSHIP_PLAN_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="loyalty/*"
            element={
              <ProtectedRoute
                element={<Loyalty />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.LOYALTY,
                      method: actionName.GET,
                      actionUrl: actionUrl.LOYALTY_CONFIG,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="reports/sales"
            element={
              <ProtectedRoute
                element={<SalesReport />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.REPORTS,
                      method: actionName.GET,
                      actionUrl: actionUrl.SALES_REPORT,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="reports/stock-aging"
            element={
              <ProtectedRoute
                element={<StockAgingReport />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.REPORTS,
                      method: actionName.GET,
                      actionUrl: actionUrl.STOCK_AGING_REPORT,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="reports/article-movement"
            element={
              <ProtectedRoute
                element={<ArticleMovementReport />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.REPORTS,
                      method: actionName.GET,
                      actionUrl: actionUrl.ARTICLE_MOVEMENT,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="accounting/trading-pl"
            element={
              <ProtectedRoute
                element={<TradingPlReport />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.ACCOUNTING,
                      method: actionName.GET,
                      actionUrl: actionUrl.TRADING_PL_REPORT,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="accounting/purchase-sale"
            element={
              <ProtectedRoute
                element={<PurchaseSaleReport />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.ACCOUNTING,
                      method: actionName.GET,
                      actionUrl: actionUrl.PURCHASE_SALE_REPORT,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="accounting/settings"
            element={
              <ProtectedRoute
                element={<AccountingSettings />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.ACCOUNTING,
                      method: actionName.GET,
                      actionUrl: actionUrl.ACCOUNTING_CONFIG,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="credit-notes/*"
            element={
              <ProtectedRoute
                element={<CreditNotes />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.CREDIT_NOTE,
                      method: actionName.GET,
                      actionUrl: actionUrl.CREDIT_NOTE_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="returns/*"
            element={
              <ProtectedRoute
                element={<Returns />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.RETURNS,
                      method: actionName.GET,
                      actionUrl: actionUrl.RETURNABLE_BILL,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="multi-challan/*"
            element={
              <ProtectedRoute
                element={<MultiChallan />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.WHOLESALE,
                      method: actionName.GET,
                      actionUrl: actionUrl.CHALLAN_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="multi-invoice/*"
            element={
              <ProtectedRoute
                element={<MultiInvoice />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.WHOLESALE,
                      method: actionName.GET,
                      actionUrl: actionUrl.INVOICE_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="stores/*"
            element={
              <ProtectedRoute
                element={<Stores />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.STORE,
                      method: actionName.GET,
                      actionUrl: actionUrl.STORE_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="users/*"
            element={
              <ProtectedRoute
                element={<Users />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.USER,
                      method: actionName.GET,
                      actionUrl: actionUrl.USER_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="roles/*"
            element={
              <ProtectedRoute
                element={<Roles />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.ROLE,
                      method: actionName.GET,
                      actionUrl: actionUrl.ROLE_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="store-type-access/*"
            element={
              <ProtectedRoute
                element={<StoreTypeAccess />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.ROLE,
                      method: actionName.GET,
                      actionUrl: actionUrl.STORE_TYPE_PERMISSIONS_GET,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="gst/*"
            element={
              <ProtectedRoute
                element={<Gst />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.GST,
                      method: actionName.GET,
                      actionUrl: actionUrl.GSTIN_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="settings/*"
            element={
              <ProtectedRoute
                element={<Settings />}
                options={{
                  optionalPermissions: [
                    {
                      module: moduleName.STORE_GST_SLABS,
                      method: actionName.GET,
                      actionUrl: actionUrl.STORE_GST_SLAB_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route
            path="profile/*"
            element={<ProtectedRoute element={<Profile />} options={{ isAlwaysAllowed: true }} />}
          />
          <Route
            path="audit-log/*"
            element={
              <ProtectedRoute
                element={<AuditLog />}
                options={{
                  mandatoryPermissions: [
                    {
                      module: moduleName.AUDIT_LOG,
                      method: actionName.GET,
                      actionUrl: actionUrl.AUDIT_LOG_LIST,
                    },
                  ],
                }}
              />
            }
          />
          <Route path="server-down" element={<ServerDown />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Outlet />
      </React.Suspense>
    </AppLayout>
  );
}

export default PrivateLayout;
